import React, { useMemo } from "react";
import {
  formatDateIntoDMYToDisplay,
  gethoursAndMins,
  splitDmyDisplayDatePrefixAndYear,
} from "Utils/utils";
import CheckBox from "../../../../components/common/checkBox/checkbox";
import { DELIVERED_SERVICE_STATUS_OPTIONS } from "Constants/constant";
import { getCaseNoteSupportItemDisplayName } from "Utils/caseNoteSupportItem";

const NdisClaimNewTableItem = (props) => {
  const {
    item,
    checkboxes,
    handleCheckboxChange,
    isUserGracePeriodOver,
    rowIndex = 0,
    showRowCheckbox = true,
  } = props;

  const rowBg = rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50";

  const dateDeliveredLabel = useMemo(() => {
    if (item.delivered_from && item.delivered_to_date) {
      return `${formatDateIntoDMYToDisplay(
        item.delivered_from
      )} - ${formatDateIntoDMYToDisplay(item.delivered_to_date)}`;
    }
    if (item.delivered_date) {
      return formatDateIntoDMYToDisplay(item.delivered_date);
    }
    return "-";
  }, [item]);

  const rateDisplay =
    item.alternative_rate && parseInt(item.alternative_rate, 10) > 0
      ? item.alternative_rate
      : item.original_rate;

  const createdAtDisplay = formatDateIntoDMYToDisplay(item.created_at);
  const createdAtParts = splitDmyDisplayDatePrefixAndYear(createdAtDisplay);

  const quantityLabel = useMemo(() => {
    if (item.quantity != null && item.quantity !== "") {
      return String(item.quantity);
    }
    const fallback = gethoursAndMins(item?.hours, item?.mints);
    return fallback && fallback !== "0h 0m" ? fallback : "—";
  }, [item]);

  const statusLabel = useMemo(() => {
    const found = DELIVERED_SERVICE_STATUS_OPTIONS.find(
      (o) => o.value === item.status
    );
    return found?.label ?? (item.status ? String(item.status) : "—");
  }, [item]);

  const supportItemDisplay = useMemo(
    () => getCaseNoteSupportItemDisplayName(item),
    [item]
  );

  return (
    <tr className={rowBg}>
      {showRowCheckbox && (
        <td
          className={`${rowBg} w-[3%] min-w-0 px-1 py-4 align-top text-sm text-gray-500`}
        >
          <div className='flex justify-center'>
            <CheckBox
              isDisabled={isUserGracePeriodOver}
              handleChange={handleCheckboxChange}
              checked={checkboxes.indexOf(item.id) !== -1}
            />
          </div>
        </td>
      )}
      <td
        className={`${rowBg} w-[6%] min-w-0 px-4 py-4 align-top text-sm text-gray-500 text-left`}
      >
        <div
          className='truncate'
          title={String(item.delivered_service_number ?? "")}
        >
          {item.delivered_service_number}
        </div>
      </td>
      <td
        className={`${rowBg} w-[11%] min-w-0 px-4 py-4 align-top text-sm text-gray-500 text-left`}
      >
        <div
          className='break-words leading-tight'
          title={String(supportItemDisplay ?? "")}
        >
          {supportItemDisplay}
        </div>
      </td>
      <td
        className={`${rowBg} w-[7%] min-w-0 px-4 py-4 align-top text-sm text-gray-500 text-left`}
      >
        <div
          className='truncate'
          title={String(item.support_item_number ?? "")}
        >
          {item.support_item_number}
        </div>
      </td>
      <td
        className={`${rowBg} w-[6%] min-w-0 px-4 py-4 align-top text-sm text-gray-500 capitalize text-left`}
      >
        <div className='truncate' title={String(item.created_by ?? "")}>
          {item.created_by}
        </div>
      </td>
      <td
        className={`${rowBg} w-[7%] min-w-0 px-4 py-4 align-top text-left text-sm text-gray-500`}
      >
        <div className='leading-tight' title={createdAtDisplay}>
          {createdAtParts.year ? (
            <>
              <span>{createdAtParts.prefix}</span>
              <wbr />
              <span className='whitespace-nowrap'>{createdAtParts.year}</span>
            </>
          ) : (
            <span className='break-words'>{createdAtDisplay}</span>
          )}
        </div>
      </td>
      <td
        className={`${rowBg} w-[10%] min-w-0 px-4 py-4 align-top text-sm text-gray-500 capitalize text-left`}
      >
        <div className='truncate' title={String(item?.participant ?? "")}>
          {item?.participant}
        </div>
      </td>
      <td
        className={`${rowBg} w-[6%] px-4 py-4 align-top text-sm text-gray-500 whitespace-nowrap text-left`}
      >
        <div className='truncate' title='NDIA Managed'>
          NDIA Managed
        </div>
      </td>
      <td
        className={`${rowBg} w-[11%] min-w-0 px-4 py-4 align-top text-sm text-gray-500 text-left`}
      >
        <div className='truncate' title={dateDeliveredLabel}>
          {dateDeliveredLabel}
        </div>
      </td>
      <td
        className={`${rowBg} w-[6%] min-w-0 px-4 py-4 align-top text-sm text-gray-500 text-left`}
      >
        <div className='truncate' title={quantityLabel}>
          {quantityLabel}
        </div>
      </td>
      <td
        className={`${rowBg} w-[6%] min-w-0 px-4 py-4 align-top text-sm text-gray-500 tabular-nums text-left`}
      >
        <div className='truncate' title={String(rateDisplay ?? "")}>
          {`$${rateDisplay ?? ""}`}
        </div>
      </td>
      <td
        className={`${rowBg} w-[7%] min-w-0 px-4 py-4 align-top text-sm text-gray-500 tabular-nums text-left`}
      >
        <div
          className='truncate'
          title={String(item.total_amount ?? "")}
        >{`$${item.total_amount || 0}`}</div>
      </td>
      <td
        className={`${rowBg} w-[7%] min-w-0 px-4 py-4 align-top text-sm text-gray-500 text-left`}
      >
        <div className='break-words leading-tight' title={statusLabel}>
          {statusLabel}
        </div>
      </td>
      <td
        className={`${rowBg} w-[3%] px-4 py-4 align-top`}
        aria-hidden='true'
      />
    </tr>
  );
};

export default NdisClaimNewTableItem;
